import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import ComputerIcon from '@mui/icons-material/Computer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ReactQuill from 'react-quill';

const PRIMARY = '#8BC34A';
const PRIMARY_DARK = '#5b9b1e';

export const localizeMetadata = (value, language) => {
  if (!value || typeof value === 'string') return value || '';
  const normalized = String(language || '').toLowerCase();
  const exactKey = Object.keys(value).find(key => key.toLowerCase() === normalized);
  const languageKey = Object.keys(value).find(key =>
    normalized.startsWith(key.slice(0, 2).toLowerCase())
  );
  return value[exactKey] || value[languageKey] || value['pt-BR'] || value['en-US'] || Object.values(value)[0] || '';
};

const layoutAccent = code => ({
  institutional_panel: '#3f56b5',
  mexx_classic: '#8BC34A',
  support_minimal: '#5f6b76',
  service_card_cta: '#7c3aed',
  document_request: '#1f5fbf',
  status_journey: '#7c3aed',
}[code] || PRIMARY);

function LayoutThumbnail({ code }) {
  const accent = layoutAccent(code);
  if (code === 'support_minimal') {
    return (
      <Box sx={{ height: 118, p: 1.25, bgcolor: '#edf5f4', borderBottom: '1px solid #e2eceb' }}>
        <Box sx={{ width: '78%', height: '100%', mx: 'auto', bgcolor: '#fff', borderTop: `4px solid ${accent}`, boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
          <Box sx={{ px: 1, height: 18, bgcolor: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ width: 25, height: 4, borderRadius: 2, bgcolor: '#d9f5ef' }} />
            <Box sx={{ width: 18, height: 6, border: `1px solid ${accent}`, borderRadius: 3 }} />
          </Box>
          <Box sx={{ px: 1.2, py: 0.9, bgcolor: '#effaf8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box><Box sx={{ width: 28, height: 3, bgcolor: accent, mb: 0.5 }} /><Box sx={{ width: 50, height: 6, bgcolor: '#25313c' }} /></Box>
            <Box sx={{ width: 26, height: 9, borderRadius: 4, bgcolor: accent }} />
          </Box>
          <Box sx={{ mx: 1.2, mt: 1, pl: 0.8, borderLeft: `3px solid ${accent}` }}>
            <Box sx={{ width: '86%', height: 4, bgcolor: '#d6dde1', mb: 0.5 }} />
            <Box sx={{ width: '68%', height: 4, bgcolor: '#e3e7e9' }} />
          </Box>
          <Box sx={{ mx: 1.2, mt: 0.9, height: 13, border: '1px solid #b9ded7', borderRadius: 1, bgcolor: '#effaf8' }} />
        </Box>
      </Box>
    );
  }
  if (code === 'service_card_cta') {
    return (
      <Box sx={{ height: 118, p: 1.25, bgcolor: '#e9eaf4', borderBottom: '1px solid #dedfea' }}>
        <Box sx={{ width: '78%', height: '100%', mx: 'auto', bgcolor: '#eef2ff', boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
          <Box sx={{ px: 1, py: 0.8, height: 24, bgcolor: '#111827' }}>
            <Box sx={{ width: 28, height: 3, bgcolor: accent, mb: 0.6 }} />
            <Box sx={{ width: 58, height: 6, bgcolor: '#fff' }} />
          </Box>
          <Box sx={{ height: 16, bgcolor: accent, opacity: 0.58 }} />
          <Box sx={{ display: 'flex', gap: 0.5, px: 1, mt: 0.8 }}>
            {[0, 1, 2].map(item => <Box key={item} sx={{ flex: 1, height: 18, bgcolor: '#fff', borderTop: `3px solid ${accent}`, borderRadius: 0.5 }} />)}
          </Box>
          <Box sx={{ mx: 1, mt: 0.7, height: 10, borderLeft: `3px solid ${accent}`, bgcolor: '#e5ddfb' }} />
        </Box>
      </Box>
    );
  }
  if (code === 'status_journey') {
    return (
      <Box sx={{ height: 118, p: 1.25, bgcolor: '#eeeef8', borderBottom: '1px solid #e0e0ec' }}>
        <Box sx={{ width: '78%', height: '100%', mx: 'auto', bgcolor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
          <Box sx={{ height: 19, bgcolor: '#17162f', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1 }}>
            <Box sx={{ width: 27, height: 4, bgcolor: '#dedbf5' }} />
            <Box sx={{ width: 24, height: 4, bgcolor: accent }} />
          </Box>
          <Box sx={{ px: 1.2, py: 0.9, bgcolor: '#f5f3ff' }}>
            <Box sx={{ width: 28, height: 3, bgcolor: accent, mb: 0.5 }} />
            <Box sx={{ width: 58, height: 6, bgcolor: '#2c2850' }} />
          </Box>
          <Box sx={{ px: 1.4, mt: 1, display: 'flex', alignItems: 'center' }}>
            {[0, 1, 2].map((item, index) => (
              <React.Fragment key={item}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: index === 0 ? '#0f766e' : index === 1 ? accent : '#cbd5e1' }} />
                {index < 2 && <Box sx={{ flex: 1, height: 2, bgcolor: index === 0 ? accent : '#d8dbe4' }} />}
              </React.Fragment>
            ))}
          </Box>
          <Box sx={{ mx: 1.2, mt: 1, height: 13, borderLeft: `3px solid ${accent}`, bgcolor: '#f5f3ff' }} />
        </Box>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        height: 118,
        p: 1.25,
        bgcolor: ['service_card_cta', 'document_request'].includes(code) ? '#eef2f7' : '#f3f3f3',
        borderBottom: '1px solid #ededed',
      }}
    >
      <Box sx={{ width: '78%', height: '100%', mx: 'auto', bgcolor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
        <Box sx={{ height: 15, bgcolor: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box sx={{ width: 34, height: 5, borderRadius: 2, bgcolor: accent }} />
        </Box>
        {code === 'service_card_cta' && <Box sx={{ height: 23, bgcolor: accent, opacity: 0.82 }} />}
        <Box sx={{ height: 18, bgcolor: code === 'support_minimal' ? '#fff' : accent, opacity: code === 'support_minimal' ? 1 : 0.92, borderTop: code === 'support_minimal' ? `2px solid ${accent}` : 0 }} />
        {code === 'institutional_panel' && <Box sx={{ mx: 1.5, my: 0.7, height: 7, borderRadius: 2, bgcolor: '#d8d8d8' }} />}
        {code === 'service_card_cta' && <Box sx={{ mx: 1.5, mt: 0.8, height: 10, borderRadius: 1, bgcolor: '#eaf2ff' }} />}
        <Box sx={{ mx: code === 'institutional_panel' ? 2 : 1.5, mt: 1, p: 0.8, bgcolor: '#fff', border: '1px solid #eeeeee' }}>
          <Box sx={{ width: '88%', height: 4, mb: 0.6, bgcolor: '#d8d8d8' }} />
          <Box sx={{ width: '70%', height: 4, mb: 0.6, bgcolor: '#e5e5e5' }} />
          <Box sx={{ width: '78%', height: 4, bgcolor: '#e5e5e5' }} />
        </Box>
        {code === 'document_request' && <Box sx={{ mx: 1.5, mt: 0.7, height: 9, borderLeft: '3px solid #e6a700', bgcolor: '#fff7e6' }} />}
        {(code === 'support_minimal' || code === 'service_card_cta') && <Box sx={{ width: 38, height: 9, borderRadius: 1, bgcolor: accent, mx: 'auto', mt: 0.8 }} />}
      </Box>
    </Box>
  );
}

export function LayoutModeSelector({ value, onChange, disabled, message }) {
  const options = [
    {
      value: 1,
      icon: <PaletteOutlinedIcon />,
      title: message('emailtemplates.mode.predefined'),
      description: message('emailtemplates.mode.predefined.help'),
    },
    {
      value: 2,
      icon: <CodeIcon />,
      title: message('emailtemplates.mode.html'),
      description: message('emailtemplates.mode.html.help'),
    },
  ];
  return (
    <Grid container spacing={2}>
      {options.map(option => {
        const selected = Number(value) === option.value;
        return (
          <Grid item xs={12} sm={6} key={option.value}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                borderWidth: selected ? 2 : 1,
                borderColor: selected ? PRIMARY : '#e0e0e0',
                bgcolor: selected ? '#f8fbf3' : '#fff',
                boxShadow: selected ? '0 0 0 3px rgba(139,195,74,.12)' : 'none',
              }}
            >
              <CardActionArea
                disabled={disabled}
                onClick={() => onChange(option.value)}
                sx={{ height: '100%', p: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5, textAlign: 'left' }}
              >
                <Box sx={{ color: selected ? PRIMARY_DARK : '#888', mt: 0.2 }}>{option.icon}</Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#3f3f3f' }}>{option.title}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: '#888', mt: 0.5 }}>{option.description}</Typography>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

export function PredefinedLayoutGallery({ layouts, selectedId, onSelect, disabled, language, message }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#555', mb: 1.25 }}>
        {message('emailtemplates.layouts.choose')}
      </Typography>
      <Grid container spacing={1.5}>
        {layouts.map(layout => {
          const selected = Number(selectedId) === Number(layout.id);
          return (
            <Grid item xs={12} sm={6} key={layout.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  overflow: 'hidden',
                  borderWidth: selected ? 2 : 1,
                  borderColor: selected ? PRIMARY : '#e0e0e0',
                  boxShadow: selected ? '0 0 0 3px rgba(139,195,74,.12)' : 'none',
                }}
              >
                <CardActionArea disabled={disabled} onClick={() => onSelect(layout.id)} sx={{ height: '100%', display: 'block' }}>
                  <LayoutThumbnail code={layout.code} />
                  <Box sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#444' }}>
                        {localizeMetadata(
                          layout.templatemetadata?.display_name || layout.name,
                          language
                        )}
                      </Typography>
                      {selected && <Chip size="small" label={message('emailtemplates.layouts.selected')} sx={{ height: 20, bgcolor: '#eef7e3', color: PRIMARY_DARK, fontSize: 10.5 }} />}
                    </Box>
                    <Typography sx={{ mt: 0.5, fontSize: 11.5, lineHeight: 1.4, color: '#888' }}>
                      {localizeMetadata(layout.templatemetadata?.description || layout.description, language)}
                    </Typography>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

const suffixFor = key => key.endsWith('_px') ? 'px' : key.endsWith('_percent') ? '%' : '';

const FIELD_TOOLTIP_KEYS = {
  preheader_text: 'emailtemplates.field.preheader.tooltip',
  notice_enabled: 'emailtemplates.field.notice.tooltip',
};

function DynamicFieldLabel({ fieldKey, label, message }) {
  const tooltipKey = FIELD_TOOLTIP_KEYS[fieldKey];
  if (!tooltipKey) return label;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <span>{label}</span>
      <Tooltip title={message(tooltipKey)} arrow placement="top">
        <HelpOutlineIcon sx={{ fontSize: 15, color: '#777', cursor: 'help' }} />
      </Tooltip>
    </Box>
  );
}

function DynamicField({ field, value, onChange, assets, disabled, language, message }) {
  const label = localizeMetadata(field.label, language);
  const help = localizeMetadata(field.help, language);
  const fieldLabel = <DynamicFieldLabel fieldKey={field.key} label={label} message={message} />;
  if (field.type === 'boolean') {
    return (
      <FormControlLabel
        control={<Switch checked={Boolean(value)} disabled={disabled} color="secondary" onChange={event => onChange(event.target.checked)} />}
        label={<Box><Typography sx={{ fontSize: 13, fontWeight: 600 }}>{fieldLabel}</Typography><Typography sx={{ fontSize: 11.5, color: '#999' }}>{help}</Typography></Box>}
      />
    );
  }
  if (field.type === 'color') {
    const normalized = /^#[0-9a-f]{6}$/i.test(value || '') ? value : '#000000';
    return (
      <Box sx={{ position: 'relative' }}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={label}
          value={value || ''}
          helperText={help}
          onChange={event => onChange(event.target.value)}
          inputProps={{ maxLength: 7, spellCheck: false }}
          sx={{ '& .MuiOutlinedInput-input': { pr: '52px' } }}
        />
        <Box
          component="input"
          type="color"
          aria-label={label}
          value={normalized}
          disabled={disabled}
          onChange={event => onChange(event.target.value)}
          sx={{
            position: 'absolute',
            zIndex: 2,
            top: 16,
            right: 10,
            transform: 'translateY(-50%)',
            width: 28,
            height: 24,
            display: 'block',
            p: 0,
            border: '1px solid #d0d0d0',
            borderRadius: '4px',
            bgcolor: 'transparent',
            cursor: disabled ? 'default' : 'pointer',
            overflow: 'hidden',
            opacity: disabled ? 0.5 : 1,
            '&::-webkit-color-swatch-wrapper': { p: 0 },
            '&::-webkit-color-swatch': { border: 0 },
            '&::-moz-color-swatch': { border: 0 },
          }}
        />
      </Box>
    );
  }
  if (field.type === 'select') {
    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{label}</InputLabel>
        <Select label={label} value={value || ''} onChange={event => onChange(event.target.value)}>
          {(field.options || []).map(option => (
            <MenuItem key={option} value={option}>
              {['left', 'center', 'right', 'justify'].includes(option)
                ? message(`emailtemplates.option.${option}`)
                : option}
            </MenuItem>
          ))}
        </Select>
        <Typography sx={{ fontSize: 11.5, color: '#999', mt: 0.5, mx: 1.75 }}>{help}</Typography>
      </FormControl>
    );
  }
  if (field.type === 'image_asset') {
    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{label}</InputLabel>
        <Select label={label} value={value || ''} onChange={event => onChange(event.target.value || null)}>
          <MenuItem value=""><em>{message('emailtemplates.assets.none')}</em></MenuItem>
          {assets.map(asset => (
            <MenuItem key={asset.id} value={asset.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="img" src={asset.url} alt="" sx={{ width: 36, height: 24, objectFit: 'contain', bgcolor: '#f5f5f5' }} />
                <span>{asset.description}</span>
              </Box>
            </MenuItem>
          ))}
        </Select>
        <Typography sx={{ fontSize: 11.5, color: '#999', mt: 0.5, mx: 1.75 }}>{help}</Typography>
      </FormControl>
    );
  }
  if (field.type === 'rich_text') {
    return (
      <Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#555', mb: 0.75 }}>{label}</Typography>
        <Box sx={{ '& .ql-toolbar': { borderRadius: '6px 6px 0 0', py: 0.4 }, '& .ql-container': { borderRadius: '0 0 6px 6px', minHeight: 78 }, '& .ql-editor': { minHeight: 78, py: 1 } }}>
          <ReactQuill
            theme="snow"
            readOnly={disabled}
            value={value || ''}
            modules={{ toolbar: [['bold', 'italic', 'underline'], ['link'], ['clean']] }}
            formats={['bold', 'italic', 'underline', 'link']}
            onChange={(content, _delta, source) => {
              if (source === 'user') onChange(content);
            }}
          />
        </Box>
        <Typography sx={{ fontSize: 11.5, color: '#999', mt: 0.5 }}>{help}</Typography>
      </Box>
    );
  }
  return (
    <TextField
      fullWidth
      size="small"
      disabled={disabled}
      required={Boolean(field.required)}
      type={field.type === 'number' ? 'number' : field.type === 'url' ? 'url' : 'text'}
      label={fieldLabel}
      value={value ?? ''}
      helperText={help}
      inputProps={{ min: field.min, max: field.max, maxLength: field.max_length }}
      InputProps={suffixFor(field.key) ? { endAdornment: <InputAdornment position="end">{suffixFor(field.key)}</InputAdornment> } : undefined}
      onChange={event => onChange(field.type === 'number' ? Number(event.target.value) : event.target.value)}
    />
  );
}

function DynamicLayoutFieldsSkeleton({ message }) {
  return (
    <Box role="status" aria-busy="true" aria-label={message('emailtemplates.layout.loading')} sx={{ mt: 2 }}>
      {[0, 1].map(section => (
        <Box key={section} sx={{ mb: section === 0 ? 1 : 0, border: '1px solid #e8e8e8', borderRadius: section === 0 ? '8px 8px 0 0' : '0 0 8px 8px', overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.2, bgcolor: '#fafafa' }}>
            <Skeleton variant="text" width={section === 0 ? 130 : 165} height={24} animation="wave" />
          </Box>
          <Grid container spacing={2} sx={{ p: 2.25 }}>
            {[0, 1, 2, 3].map(field => (
              <Grid item xs={12} sm={6} key={field}>
                <Skeleton variant="rounded" width="100%" height={42} animation="wave" />
                <Skeleton variant="text" width={field % 2 ? '62%' : '78%'} height={16} animation="wave" sx={{ mt: 0.5 }} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}

export function DynamicLayoutFields({ layout, values, onChange, onReset, assets, disabled, language, message, loading = false }) {
  if (loading) return <DynamicLayoutFieldsSkeleton message={message} />;
  if (!layout?.templatemetadata) return null;
  const metadata = layout.templatemetadata;
  const orderedGroups = [...(metadata.groups || [])].sort((a, b) => a.display_order - b.display_order);
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
        <Button size="small" startIcon={<RestartAltIcon />} disabled={disabled} onClick={onReset} sx={{ textTransform: 'none', color: '#666' }}>
          {message('emailtemplates.customization.reset')}
        </Button>
      </Box>
      {orderedGroups.map((group, index) => {
        const fields = (metadata.fields || []).filter(item => item.group === group.key);
        return (
          <Accordion key={group.key} defaultExpanded={index < 2} disableGutters elevation={0} sx={{ border: '1px solid #e8e8e8', '&:not(:last-child)': { borderBottom: 0 }, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#fafafa', minHeight: 48 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#555' }}>{localizeMetadata(group.label, language)}</Typography>
              <Chip size="small" label={fields.length} sx={{ ml: 1, height: 19, fontSize: 10, bgcolor: '#eee' }} />
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2.5 }}>
              <Grid container spacing={2.25}>
                {fields.map(item => (
                  <Grid item xs={12} sm={item.type === 'rich_text' ? 12 : 6} key={item.key}>
                    <DynamicField field={item} value={values?.[item.key]} onChange={value => onChange(item.key, value)} assets={assets} disabled={disabled} language={language} message={message} />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

export const validateHtmlDocument = (html, message = id => id) => {
  const diagnostics = [];
  const value = String(html || '');
  const lineAt = index => value.slice(0, index).split(/\r\n|\r|\n/).length;
  const addMatches = (pattern, messageId, valuesFromMatch) => {
    let match = pattern.exec(value);
    while (match) {
      diagnostics.push(message(messageId, valuesFromMatch(match, lineAt(match.index))));
      match = pattern.exec(value);
    }
  };
  const voidTags = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
  const structureSource = value.replace(/<!--[\s\S]*?-->/g, comment => comment.replace(/[^\r\n]/g, ' '));
  const tagPattern = /<\s*(\/?)\s*([a-z][a-z0-9:-]*)\b[^>]*>/gi;
  const openTags = [];
  let tagMatch = tagPattern.exec(structureSource);

  while (tagMatch) {
    const closing = tagMatch[1] === '/';
    const tag = tagMatch[2].toLowerCase();
    const line = lineAt(tagMatch.index);
    const selfClosing = /\/\s*>$/.test(tagMatch[0]);
    if (closing) {
      const openingIndex = openTags.map(item => item.tag).lastIndexOf(tag);
      if (openingIndex < 0) {
        diagnostics.push(message('emailtemplates.validation.html.structure.unexpected', { tag: `</${tag}>`, line }));
      } else {
        openTags.slice(openingIndex + 1).reverse().forEach(item => {
          diagnostics.push(message('emailtemplates.validation.html.structure.nested', {
            tag: `<${item.tag}>`,
            openLine: item.line,
            closingTag: `</${tag}>`,
            line,
          }));
        });
        openTags.length = openingIndex;
      }
    } else if (!selfClosing && !voidTags.has(tag)) {
      openTags.push({ tag, line });
    }
    tagMatch = tagPattern.exec(structureSource);
  }

  openTags.reverse().forEach(item => {
    diagnostics.push(message('emailtemplates.validation.html.structure.unclosed', {
      tag: `<${item.tag}>`,
      line: item.line,
    }));
  });

  if (value.length > 100000) diagnostics.push(message('emailtemplates.validation.html.length'));
  addMatches(
    /<\s*\/?\s*(script|iframe|frame|frameset|object|embed|form|svg|template|base)\b/gi,
    'emailtemplates.validation.html.tag',
    (match, line) => ({ tag: `<${match[1].toLowerCase()}>`, line })
  );
  addMatches(
    /\s(on[a-z0-9_-]+)\s*=/gi,
    'emailtemplates.validation.html.event',
    (match, line) => ({ attribute: match[1].toLowerCase(), line })
  );
  addMatches(
    /(javascript\s*:|vbscript\s*:|@import\b|expression\s*\(|url\s*\()/gi,
    'emailtemplates.validation.html.css',
    (match, line) => ({ rule: match[1].replace(/\s+/g, ''), line })
  );
  addMatches(
    /@\{[^}\r\n]+\}/g,
    'emailtemplates.validation.html.placeholder.syntax',
    (match, line) => ({ placeholder: match[0], line })
  );
  const assetMarkerPattern = /mexx-email-asset:\/\/[1-9]\d*/gi;
  let assetMarkerMatch = assetMarkerPattern.exec(value);
  while (assetMarkerMatch) {
    const tagStart = value.lastIndexOf('<', assetMarkerMatch.index);
    const tagEnd = value.indexOf('>', assetMarkerMatch.index);
    const containingTag = tagStart >= 0 && tagEnd >= 0
      ? value.slice(tagStart, tagEnd + 1)
      : '';
    const markerInImageSource = /^<\s*img\b/i.test(containingTag)
      && /\bsrc\s*=\s*(["'])mexx-email-asset:\/\/[1-9]\d*\1/i.test(containingTag);
    if (!markerInImageSource) {
      diagnostics.push(message('emailtemplates.validation.html.asset.marker', {
        marker: assetMarkerMatch[0],
        line: lineAt(assetMarkerMatch.index),
      }));
    }
    assetMarkerMatch = assetMarkerPattern.exec(value);
  }
  const bodyCount = (value.match(/<body\b/gi) || []).length;
  if (bodyCount !== 1) diagnostics.push(message('emailtemplates.validation.html.body', { count: bodyCount }));
  if (typeof DOMParser !== 'undefined') {
    const document = new DOMParser().parseFromString(value, 'text/html');
    document.querySelectorAll('img[src]').forEach(image => {
      const source = (image.getAttribute('src') || '').trim();
      const completePlaceholder = /^\{\{[^{}]+\}\}$/.test(source);
      let validHttps = false;
      try {
        const parsedSource = new URL(source);
        validHttps = parsedSource.protocol === 'https:' && Boolean(parsedSource.hostname);
      } catch (_error) {
        validHttps = false;
      }
      if (!validHttps
          && !/^mexx-email-asset:\/\/[1-9]\d*$/i.test(source)
          && !completePlaceholder) {
        diagnostics.push(message('emailtemplates.validation.html.image', { source: `src="${source}"` }));
      }
    });
    document.querySelectorAll('a[href]').forEach(link => {
      const href = (link.getAttribute('href') || '').trim();
      const completePlaceholder = /^\{\{[^{}]+\}\}$/.test(href);
      let validLink = false;
      try {
        const parsedHref = new URL(href);
        validLink = ['http:', 'https:', 'mailto:'].includes(parsedHref.protocol)
          && (parsedHref.protocol === 'mailto:' || Boolean(parsedHref.hostname));
      } catch (_error) {
        validLink = false;
      }
      if (!validLink && !completePlaceholder) {
        diagnostics.push(message('emailtemplates.validation.html.link', { source: `href="${href}"` }));
      }
    });
  }
  return [...new Set(diagnostics)];
};

export const HtmlCodeEditor = forwardRef(function HtmlCodeEditor({ value, onChange, disabled, message, onDiagnostics }, ref) {
  const editorRef = useRef(null);
  const [editorScrollTop, setEditorScrollTop] = useState(0);
  const lineCount = Math.max(1, String(value || '').split(/\r\n|\r|\n/).length);
  const lineNumbers = Array.from({ length: lineCount }, (_item, index) => index + 1).join('\n');
  const gutterWidth = Math.max(44, 24 + String(lineCount).length * 8);

  useEffect(() => {
    onDiagnostics(validateHtmlDocument(value, message));
  }, [value]);

  useImperativeHandle(ref, () => ({
    insertText(text) {
      const editor = editorRef.current;
      const html = value || '';
      const selectionStart = editor ? editor.selectionStart : html.length;
      const selectionEnd = editor ? editor.selectionEnd : selectionStart;
      const nextValue = `${html.slice(0, selectionStart)}${text}${html.slice(selectionEnd)}`;
      const nextCursorPosition = selectionStart + text.length;

      onChange(nextValue);
      window.requestAnimationFrame(() => {
        if (!editorRef.current) return;
        editorRef.current.focus();
        editorRef.current.setSelectionRange(nextCursorPosition, nextCursorPosition);
      });
    },
  }), [onChange, value]);

  return (
    <Box sx={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fafafa', borderBottom: '1px solid #e5e5e5' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CodeIcon sx={{ fontSize: 18, color: '#777' }} /><Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#555' }}>{message('emailtemplates.html.editor')}</Typography></Box>
        <Chip size="small" label="HTML" sx={{ height: 20, fontSize: 10.5 }} />
      </Box>
      <Box sx={{ position: 'relative' }}>
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            zIndex: 1,
            top: 0,
            bottom: 0,
            left: 0,
            width: gutterWidth,
            overflow: 'hidden',
            pointerEvents: 'none',
            bgcolor: '#f6f7f8',
            borderRight: '1px solid #e1e3e5',
            color: '#8a9096',
          }}
        >
          <Box
            component="pre"
            sx={{
              m: 0,
              pt: '14px',
              pr: '9px',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: 13,
              lineHeight: 1.55,
              textAlign: 'right',
              userSelect: 'none',
              transform: `translateY(-${editorScrollTop}px)`,
            }}
          >
            {lineNumbers}
          </Box>
        </Box>
        <TextField
          inputRef={editorRef}
          fullWidth
          multiline
          minRows={10}
          maxRows={18}
          variant="outlined"
          value={value || ''}
          onChange={event => onChange(event.target.value)}
          disabled={disabled}
          inputProps={{
            spellCheck: false,
            wrap: 'off',
            onScroll: event => setEditorScrollTop(event.currentTarget.scrollTop),
            'aria-label': message('emailtemplates.html.editor'),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              p: 0,
              borderRadius: 0,
              alignItems: 'flex-start',
            },
            '& .MuiOutlinedInput-notchedOutline': { border: 0 },
            '& textarea': {
              boxSizing: 'border-box',
              p: `14px 14px 14px ${gutterWidth + 12}px !important`,
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: 13,
              lineHeight: 1.55,
              tabSize: 2,
              whiteSpace: 'pre',
              overflowWrap: 'normal',
              overflowX: 'auto !important',
            },
          }}
        />
      </Box>
    </Box>
  );
});

export function HtmlRequirements({ message }) {
  const rules = [
    'emailtemplates.html.rules.document',
    'emailtemplates.html.rules.tags',
    'emailtemplates.html.rules.events',
    'emailtemplates.html.rules.urls',
    'emailtemplates.html.rules.css',
    'emailtemplates.html.rules.placeholders',
  ];

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        mb: 1.5,
        border: '1px solid #c9dff2',
        borderRadius: '8px !important',
        bgcolor: '#f7fbff',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#3977a8' }} />}
        sx={{
          minHeight: 46,
          px: 1.5,
          '&.Mui-expanded': { minHeight: 46 },
          '& .MuiAccordionSummary-content': { my: 0.75, alignItems: 'center', gap: 1 },
          '& .MuiAccordionSummary-content.Mui-expanded': { my: 0.75 },
        }}
      >
        <HelpOutlineIcon sx={{ flexShrink: 0, fontSize: 19, color: '#3977a8' }} />
        <Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#315e80' }}>
            {message('emailtemplates.html.rules.title')}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#668197' }}>
            {message('emailtemplates.html.rules.hint')}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
        <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.65 }}>
          {rules.map(rule => (
            <Typography component="li" key={rule} sx={{ fontSize: 11.5, lineHeight: 1.45, color: '#4d6170' }}>
              {message(rule)}
            </Typography>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

const normalizePreviewHtmlEntities = html => String(html || '').replace(
  /&amp;(#(?:x[0-9a-f]+|\d+)|[a-z][a-z0-9]+);/gi,
  '&$1;'
);

function EmailPreviewSkeleton({ message }) {
  return (
    <Box role="status" aria-busy="true" aria-label={message('emailtemplates.layout.loading')} sx={{ width: 640, maxWidth: 'calc(100% - 32px)', minHeight: 540, bgcolor: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,.08)' }}>
      <Skeleton variant="rectangular" height={72} animation="wave" />
      <Box sx={{ px: 4, pt: 4 }}>
        <Skeleton variant="text" width="34%" height={20} animation="wave" />
        <Skeleton variant="text" width="72%" height={36} animation="wave" sx={{ mb: 2.5 }} />
        <Box sx={{ display: 'flex', gap: 1.25, mb: 3 }}>
          {[0, 1, 2].map(item => <Skeleton key={item} variant="rounded" height={62} sx={{ flex: 1 }} animation="wave" />)}
        </Box>
        <Skeleton variant="text" width="96%" height={20} animation="wave" />
        <Skeleton variant="text" width="88%" height={20} animation="wave" />
        <Skeleton variant="text" width="70%" height={20} animation="wave" />
        <Skeleton variant="rounded" width="100%" height={92} animation="wave" sx={{ mt: 2.5 }} />
        <Skeleton variant="rounded" width={150} height={40} animation="wave" sx={{ mt: 3, mx: 'auto' }} />
      </Box>
    </Box>
  );
}

export function EmailDesignerPreview({ preview, loading, layoutChanging = false, layoutUpdating = false, error, onRefresh, message }) {
  const [device, setDevice] = useState('desktop');
  const width = device === 'mobile' ? 390 : 760;
  return (
    <Box sx={{ position: { xs: 'static', lg: 'sticky' }, top: { lg: 56 }, zIndex: 2, height: 'fit-content' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#444' }}>{message('emailtemplates.preview.title')}</Typography>
          <Typography sx={{ fontSize: 11.5, color: '#999' }}>{message('emailtemplates.preview.help')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <ToggleButtonGroup exclusive size="small" value={device} onChange={(_event, next) => next && setDevice(next)}>
            <ToggleButton value="desktop" aria-label={message('emailtemplates.preview.desktop')}><ComputerIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="mobile" aria-label={message('emailtemplates.preview.mobile')}><PhoneIphoneIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title={message('emailtemplates.preview.refresh')}>
            <span><Button variant="outlined" disabled={loading || layoutUpdating} onClick={onRefresh} sx={{ minWidth: 38, width: 38, height: 38, p: 0, borderColor: '#d7d7d7', color: PRIMARY_DARK }}><RefreshIcon fontSize="small" /></Button></span>
          </Tooltip>
        </Box>
      </Box>
      {error && !layoutUpdating && (
        <Alert severity="error" sx={{ mb: 1, fontSize: 12 }}>
          {Array.isArray(error) ? (
            <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.4 }}>
              {error.map(item => <li key={item}>{item}</li>)}
            </Box>
          ) : error}
        </Alert>
      )}
      <Box sx={{ border: '1px solid #dcdcdc', borderRadius: '12px', overflow: 'hidden', bgcolor: '#ececec', boxShadow: '0 8px 24px rgba(0,0,0,.07)' }}>
        <Box sx={{ px: 2, py: 1.25, bgcolor: '#fff', borderBottom: '1px solid #e5e5e5' }}>
          <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.06em', color: '#999' }}>{message('emailtemplates.subject')}</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#444', mt: 0.25 }}>{preview?.subject || message('emailtemplates.preview.subject.empty')}</Typography>
        </Box>
        <Box sx={{ minHeight: 580, py: 2, overflowX: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
          {layoutChanging || (loading && !layoutUpdating) ? (
            <EmailPreviewSkeleton message={message} />
          ) : preview?.html ? (
            <Box sx={{ width, maxWidth: '100%', height: 560, bgcolor: '#fff', transition: 'width .2s ease', boxShadow: '0 2px 12px rgba(0,0,0,.1)' }}>
              <iframe title={message('emailtemplates.preview.title')} sandbox="" srcDoc={normalizePreviewHtmlEntities(preview.html)} style={{ display: 'block', width: '100%', height: '100%', border: 0, background: '#fff' }} />
            </Box>
          ) : (
            <Box sx={{ pt: 12, px: 4, textAlign: 'center' }}><Typography sx={{ fontSize: 13, color: '#888' }}>{message('emailtemplates.preview.empty')}</Typography></Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
